import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { IconRocket } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';

import { ConfirmationModal } from 'ui';

import { templateTypes } from '../../core/constants';
import appSettings from '../../app-settings';
import Button from '../buttons/Button';
import AlertMessage from '../alerts/AlertMessage';
import { useAccount } from '../../store/account/hooks';
import { useDomains } from '../../store/domains/hooks';

const EMAIL_TEMPLATES_URL = `${appSettings.baseUrl}/email-templates`;
const ACCOUNTS_URL = `${appSettings.baseUrl}/accounts`;

const REQUIRED_TEMPLATE_COUNT = 4;
const DONATION_PAGE_URL_REGEX = /https(.*?)campaignId=/g;

const LaunchWelcomeSeriesButton = ({ onSuccess, onError, showAlerts = true }) => {
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [emailsMissingDonateButton, setEmailsMissingDonateButton] = useState([]);
  const { account, getAccount } = useAccount();
  const { authenticatedDomain, getDomains } = useDomains();

  const templateCount = templates.filter((t) => t.templateId).length;
  const isLaunched = account?.welcomeSeriesLaunched;
  const hasAuthenticatedDomain = !!authenticatedDomain;
  const canLaunch = templateCount >= REQUIRED_TEMPLATE_COUNT && !isLaunched;

  const getRecurringTemplates = useCallback(async () => {
    try {
      const { data: savedTemplates } = await axios.get(EMAIL_TEMPLATES_URL, {
        params: { type: templateTypes.recurring },
      });
      setTemplates(savedTemplates);
    } catch (err) {
      Sentry.captureException(err);
      // Don't show error for template check, just keep templates empty
    }
  }, []);

  useEffect(() => {
    getRecurringTemplates();
    getDomains();
  }, [getRecurringTemplates, getDomains]);

  const checkTemplatesForDonateButtons = useCallback(() => {
    const missingDonateButton = templates
      .filter((template) => template.templateId && template.design)
      .filter((template) => template.design.search(DONATION_PAGE_URL_REGEX) === -1)
      .sort((a, b) => a.order - b.order)
      .map((template) => `Welcome Email #${template.order + 1}`);
    return missingDonateButton;
  }, [templates]);

  const handleLaunchWelcomeSeries = async () => {
    if (!hasAuthenticatedDomain) {
      const errorMessage = 'Unable to launch Welcome Series - please verify your domain in Settings > Domain Auth.';
      setError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    const missingDonateButtons = checkTemplatesForDonateButtons();
    if (missingDonateButtons.length > 0) {
      setEmailsMissingDonateButton(missingDonateButtons);
      setConfirmModalOpen(true);
      return;
    }

    await executeLaunch();
  };

  const executeLaunch = async () => {
    setConfirmModalOpen(false);
    setLaunchLoading(true);
    setError(false);
    setSuccess(false);
    try {
      await axios.post(`${ACCOUNTS_URL}/welcome-series/launch`);
      const successMessage = 'Welcome Series launched successfully!';
      setSuccess(successMessage);
      // Refresh account to get updated welcomeSeriesLaunched status
      getAccount();
      onSuccess?.(successMessage);
    } catch (err) {
      Sentry.captureException(err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to launch Welcome Series';
      setError(errorMessage);
      onError?.(errorMessage);
    }
    setLaunchLoading(false);
  };

  return (
    <>
      {!isLaunched && (
        <Button
          title={canLaunch ? 'Launch Welcome Series' : `${templateCount}/${REQUIRED_TEMPLATE_COUNT} emails ready`}
          LeftIcon={IconRocket}
          onClick={handleLaunchWelcomeSeries}
          disabled={!canLaunch || launchLoading}
          loading={launchLoading}
          color="primary"
        />
      )}

      {isLaunched && (
        <div className={`w-fit rounded border border-green-100 bg-green-50 px-1.5 py-0.5 text-green-500`}>
          <p className="text-caption1 font-semibold uppercase">Launched</p>
        </div>
      )}

      {showAlerts && (
        <>
          <AlertMessage
            open={!!error}
            message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
            onClose={() => setError(false)}
            severity="error"
          />

          <AlertMessage open={!!success} message={success} onClose={() => setSuccess(false)} severity="success" />
        </>
      )}

      <ConfirmationModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={executeLaunch}
        message={`The following emails do not contain a donate button: ${emailsMissingDonateButton.join(', ')}. Are you sure you want to launch the Welcome Series without them?`}
        loading={launchLoading}
      />
    </>
  );
};

LaunchWelcomeSeriesButton.propTypes = {
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  showAlerts: PropTypes.bool,
};

export default LaunchWelcomeSeriesButton;
